import { useEffect, useState } from 'react';
import { Button, LinearProgress, Tabs, SelectMultiple } from '../../tailwindcomponents';
import { shopPlanOptions } from '../../../src/properties/shopProperties';
import hangarPlanApi from '../../api/hangarPlanApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import { DateTime } from 'luxon';
import MobiscrollCalendar from '../../components/Calendar/MobiscrollCalendar';
import { MbscCalendarEvent } from '@mobiscroll/react';
import { EventInfo } from '../../types/mobiScroll';
import WorkPackageDetailsDialog from '../HangarPlan/WorkPackageDetailsDialog';
import { safeLocalStorage } from '../../utils/safeLocalStorage';
import { palette } from '../../theme/palette';

const statusOptions = [
    { id: 0, name: "COMPLETE" },
    { id: 1, name: "ACTV" },
    { id: 2, name: "COMMIT" },
    { id: 3, name: "IN WORK" },
];

const defaultShopPlanFilters = Object.values(shopPlanOptions) as
  (typeof shopPlanOptions)[keyof typeof shopPlanOptions][];

const storedShopPlanFilters = safeLocalStorage.get("shopPlanFilters");
const storedShopPlanStatus = safeLocalStorage.get("shopPlanStatusFilters");

if (storedShopPlanStatus === null || storedShopPlanStatus === undefined) {
    safeLocalStorage.set("shopPlanStatusFilters", JSON.stringify(statusOptions.map(option => option.id)));
}

if (storedShopPlanFilters === null || storedShopPlanFilters === undefined) {
    safeLocalStorage.set("shopPlanFilters", JSON.stringify(defaultShopPlanFilters));
}
const getShopPlanLocationFiltersFromLocalStorage = (): string[] => {
    const storedFilters = safeLocalStorage.get("shopPlanFilters");
    if (storedFilters) {
        return JSON.parse(storedFilters);
    } else {
        return defaultShopPlanFilters;
    }
};

const getShopPlanStatusFiltersFromLocalStorage = (): number[] => {
    const storedFilters = safeLocalStorage.get("shopPlanStatusFilters");
    if (storedFilters) {
        return JSON.parse(storedFilters);
    } else {
        return statusOptions.map(option => option.id);
    }
};

const ReturnStatusColor = (status: string, unassigned: boolean | undefined) => {

    if (unassigned === true) {
        return palette.status.unassigned
    }
    switch (status) {
        case ("COMPLETE"):
            return palette.success;
        case ("ACTV"):
            return palette.secondary;
        case ("PAUSE"):
            return "";
        case ("COMMIT"):
            return palette.status.commit;
        default:
            return palette.status.inwork;
    }
}

const ShopPlan = () => {
    const [loading, setLoading] = useState(false);
    const [calendarData, setCalendarData] = useState<EventInfo[]>([]);
    const me = useSelector((state: RootState) => state.user.me);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<MbscCalendarEvent>();
    const [selectedShops, setSelectedShops] = useState<string[]>(() => {
        return getShopPlanLocationFiltersFromLocalStorage();
    });
    const [selectedStatusOptions, setSelectedStatusOptions] = useState<number[]>(() => {
        return getShopPlanStatusFiltersFromLocalStorage();
    });
    
    useEffect(() => {
        if (!dialogOpen) refresh();
    }, [dialogOpen]);

    const refresh = async () => {
        setLoading(true);

        let fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 90);
        let toDate = new Date();
        toDate.setDate(toDate.getDate() + 90);

        const tempSelectedStatus = selectedStatusOptions.map(option => statusOptions[option].name);

        await hangarPlanApi.GetHangarPlanPackages(fromDate, toDate, tempSelectedStatus, selectedShops, true).then(res => {
            setLoading(false);
            const data = res.data;
            const tasks: EventInfo[] = [];
            data.forEach(packet => {
                const startDate = new Date(packet.start_date);
                const endDate = new Date(packet.end_date);

                const startDt = DateTime.fromJSDate(startDate);
                const endDt = DateTime.fromJSDate(endDate);

                const formattedStart = startDt.toFormat('dd.MM.yyyy HH:mm');
                const formattedEnd = endDt.toFormat('dd.MM.yyyy HH:mm');

                // Adjust startDate to the nearest 4-hour block
                const startHour = startDate.getHours();
                const adjustedStartHour = Math.floor(startHour / 4) * 4;
                startDate.setHours(adjustedStartHour, 0, 0, 0);

                // Adjust endDate to the nearest 4-hour block
                let endHour = endDate.getHours();
                if (startHour === endHour) {
                    endHour = endHour + 1;
                }
                const adjustedEndHour = Math.ceil((endHour + endDate.getMinutes() / 60) / 4) * 4;
                endDate.setHours(adjustedEndHour, 0, 0, 0);

                tasks.push({
                    "start": startDate,
                    "end": endDate,
                    "id": packet.wo_barcode,
                    "title": packet.tail_number + " " + packet.wo_name,
                    "resource": packet.wo_location,
                    "color": ReturnStatusColor(packet.wo_status, undefined),
                    "wo_status": packet.wo_status,
                    "parts_amounts": packet.basket,
                    "parts_available": packet.avail_basket,
                    "start_date_text": formattedStart,
                    "end_date_text": formattedEnd,
                    "assigned_employees": packet.assigned_employees,
                    "mxi_man_hours": packet.mxi_man_hours,
                    "mxi_man_hours_days": packet.mxi_man_hours_days,
                    "wo_includes_clean_task": packet.wo_includes_clean_task,
                    "need_cert": packet.need_cert,
                    "heavy_bool": packet.heavy_bool,
                });
            });
            setCalendarData(tasks);
        });
    }

    const onEventClicked = (event: MbscCalendarEvent) => {
        setSelectedEvent(event.event);
        setDialogOpen(true);
    };

    const handleDialogClose = () => setDialogOpen(false);

    return (
        <div className="w-full flex flex-1 flex-col min-h-0">
            {loading && <LinearProgress />}
            <div className="flex flex-1 flex-col min-h-0">
                <Tabs
                    tabNames={["Shop Plan"]}
                    tabValue={0}
                    onChangeTabValue={() => { }}
                />
                <div className="ds-filter-bar shrink-0">
                    <div>
                        <SelectMultiple
                            options={Object.values(shopPlanOptions)}
                            onSelectedItem={(e: any) => {
                                setSelectedShops(e.target.value);
                                safeLocalStorage.set("shopPlanFilters", JSON.stringify(e.target.value));
                            }}
                            selectedItemsString={selectedShops}
                            width={200}
                            filter_by_text={"Filter by shops"}
                        />
                    </div>
                    <div>
                        <SelectMultiple
                            optionsObject={statusOptions}
                            onSelectedItem={(e: any) => {
                                setSelectedStatusOptions(e.target.value);
                                safeLocalStorage.set("shopPlanStatusFilters", JSON.stringify(e.target.value));
                            }}
                            selectedItems={selectedStatusOptions}
                            width={200}
                            filter_by_text={"Filter by status"}
                        />
                    </div>
                    <Button onClick={() => refresh()} variant="outlined" className="self-end">Refresh</Button>
                </div>
                <div className="w-full flex flex-1 flex-col min-h-0">
                    <div className="flex flex-1 flex-col min-h-0 [&_.mbsc-schedule-event-range.mbsc-ios]:!hidden">
                        <MobiscrollCalendar
                            calendarTasks={calendarData}
                            resources={selectedShops}
                            onEventClick={onEventClicked}
                            isShopPlanView={true}
                            isShortTermView={false}
                        />
                        {dialogOpen && selectedEvent && (
                            <WorkPackageDetailsDialog
                                location="SHOPS"
                                dialogOpen={dialogOpen}
                                onClose={() => handleDialogClose()}
                                selectedEvent={selectedEvent}
                                isAdmin={me.user_clearence_groups.includes("Shop Plan Edit")}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ShopPlan;